import { Calendar, MapPin } from "lucide-react";
import type { ReactNode } from "react";

import { BrandMark } from "components/BrandMark";
import { ThemeToggle } from "components/ThemeToggle";
import { formatEventDate } from "components/EventMeta";

import {
  HERO_OVERLAY,
  HeroVideo,
  headlineFontSize,
} from "../event-experience/EventHero";
import { splitHeadline } from "./landing";
import type { PublicEvent } from "./schemas";

/**
 * The action sits on a dark hero but keeps its own content. A card (the sign-in
 * band, the door-code gate) brings its own surface and is left alone. A bare
 * button, the register button, is the main button (variant="cta") and this makes
 * it large: tall, full width and round. The focus ring is white, which shows on
 * the dark ground in both themes.
 */
const ACTION_SLOT =
  "w-full max-w-md [&>button]:min-h-14 [&>button]:w-full [&>button]:rounded-full [&>button]:px-8 [&>button]:text-lg [&>button]:font-bold [&>button:focus-visible]:ring-white [&>button:focus-visible]:ring-offset-hb-dusk-deep";

/**
 * The bold opening of the public event page: the event image or a muted loop
 * behind a dark overlay, the kicker, the stacked headline with its last line in
 * the accent, the subline, the place and dates, and the page's action as the
 * large primary control.
 *
 * `variant` decides the chrome and the headings:
 * - "page" is the bare signed-out page. It is full-bleed with the brand bar on
 *   top, and the event title is the page's one h1: the headline's own words
 *   when there is none, a small line above the headline otherwise (hidden when
 *   a kicker already names the event).
 * - "shell" is the card inside the application shell, whose header already
 *   carries the title as the page's h1. The headline is an h2 and the title is
 *   not repeated.
 *
 * The action node is rendered exactly once, here: it may hold a form with ids
 * and state, which a second copy further down the page would duplicate.
 */
export function PublicHero({
  event,
  action,
  variant,
}: {
  event: PublicEvent;
  action?: ReactNode;
  variant: "page" | "shell";
}) {
  const { lines, accentIndex } = splitHeadline(event.heroHeadline);
  const kicker = event.heroKicker?.trim();
  const subline = event.heroSubline?.trim();
  const video = event.heroVideo?.trim();
  const objectPosition = `${event.imageFocalX ?? 50}% ${event.imageFocalY ?? 50}%`;
  const page = variant === "page";

  return (
    <section
      className={`relative isolate flex flex-col overflow-hidden bg-dusk-hero pl-[max(1.25rem,env(safe-area-inset-left))] pr-[max(1.25rem,env(safe-area-inset-right))] text-white sm:pl-[max(2rem,env(safe-area-inset-left))] sm:pr-[max(2rem,env(safe-area-inset-right))] ${
        page
          ? "min-h-[min(52rem,calc(100svh-3rem))]"
          : "min-h-[min(48rem,calc(100svh-9rem))] rounded-2xl"
      } ${
        // Under the content, out of the way of the 44px pause button.
        video
          ? "pb-[calc(max(0.75rem,env(safe-area-inset-bottom))+3.5rem)]"
          : "pb-[max(1.75rem,env(safe-area-inset-bottom))]"
      }`}
      data-testid="public-hero"
    >
      {!video && event.image && (
        <img
          src={event.image}
          alt=""
          className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover"
          style={{ objectPosition }}
        />
      )}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-10"
        style={{ background: HERO_OVERLAY }}
        data-testid="public-hero-overlay"
      />

      {page && (
        // A signed-out visitor has no rail, but still needs to know which
        // product this is.
        <div
          className="relative z-20 mx-auto flex min-h-14 w-full max-w-4xl items-center gap-3 pt-[env(safe-area-inset-top)]"
          data-testid="public-hero-brand-bar"
        >
          <BrandMark size="sm" className="rounded-sm bg-white p-1" />
          <p className="font-display text-lg font-semibold">
            Holtzbrinck Connect
          </p>
          <ThemeToggle className="ml-auto text-white hover:bg-white/10 focus-visible:ring-white" />
        </div>
      )}

      <div
        className="relative z-20 mx-auto mt-auto flex w-full max-w-4xl flex-col items-start gap-4 pt-16 sm:gap-5"
        style={{ containerType: "inline-size" }}
        data-testid="public-hero-content"
      >
        {page && lines.length > 0 && (
          <h1
            className={
              kicker
                ? "sr-only"
                : "text-sm uppercase tracking-[0.16em] wrap-anywhere"
            }
          >
            {event.title}
          </h1>
        )}
        {kicker && (
          <p
            className="text-sm uppercase tracking-[0.16em] wrap-anywhere"
            data-testid="public-hero-kicker"
          >
            {kicker}
          </p>
        )}
        {lines.length > 0 ? (
          <h2
            className="font-display font-normal leading-[0.9] tracking-tight wrap-anywhere"
            style={{ fontSize: headlineFontSize(lines) }}
          >
            {lines.map((line, index) =>
              index === accentIndex ? (
                <em
                  key={index}
                  className="block font-semibold italic text-hb-yellow"
                >
                  {line}
                </em>
              ) : (
                <span key={index} className="block">
                  {line}
                </span>
              ),
            )}
          </h2>
        ) : (
          page && (
            <h1
              className="font-display font-normal leading-[0.9] tracking-tight wrap-anywhere"
              style={{ fontSize: headlineFontSize([event.title]) }}
            >
              {event.title}
            </h1>
          )
        )}
        {subline && (
          <p
            className="text-xl leading-snug wrap-anywhere sm:text-2xl"
            data-testid="public-hero-subline"
          >
            {subline}
          </p>
        )}
        <div
          className="flex min-w-0 max-w-full flex-wrap gap-x-5 gap-y-2 text-base"
          data-testid="public-hero-meta"
        >
          {event.location && (
            <span className="flex min-w-0 max-w-full items-center gap-1.5">
              <MapPin className="h-[18px] w-[18px] shrink-0" aria-hidden />
              <span
                className="min-w-0 wrap-anywhere"
                data-testid="public-hero-meta-place"
              >
                {event.location}
              </span>
            </span>
          )}
          {event.startDate && (
            <span className="flex min-w-0 max-w-full items-center gap-1.5">
              <Calendar className="h-[18px] w-[18px] shrink-0" aria-hidden />
              <span className="min-w-0 wrap-anywhere">
                {formatEventDate(event.startDate)}
                {event.endDate && event.endDate !== event.startDate && (
                  <> &ndash; {formatEventDate(event.endDate)}</>
                )}
              </span>
            </span>
          )}
        </div>
        {action && (
          <div
            className={`${ACTION_SLOT} mt-2`}
            data-testid="public-hero-action"
          >
            {action}
          </div>
        )}
      </div>

      {video && (
        <HeroVideo
          toggleTestId="public-hero-video-toggle"
          src={video}
          poster={event.image ?? undefined}
          objectPosition={objectPosition}
        />
      )}
    </section>
  );
}
